import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { store } from '@core/store';
import { logout, setTokens, adminLogout } from '@core/store/slices/authSlice';

// Import token utilities from separate file to avoid circular dependency
import {
    getStoredToken,
    getStoredAdminToken,
    getStoredRefreshToken,
    storeTokens,
    storeAdminToken,
    clearTokens,
} from './token-storage';

// Re-export token utilities for backward compatibility
export {
    getStoredToken,
    getStoredAdminToken,
    getStoredRefreshToken,
    storeTokens,
    storeAdminToken,
    clearTokens,
};

/**
 * Check if we're in an admin context
 * Checks both the API endpoint URL and the current browser location
 */
const isAdminContext = (apiUrl?: string): boolean => {
    // Check API URL for admin-related paths
    const isAdminApiUrl = apiUrl?.includes('/admin') ||
        apiUrl?.includes('/dashboard') ||
        apiUrl?.includes('/sales');

    // Check browser location for admin dashboard
    const isAdminBrowserPath = typeof window !== 'undefined' &&
        window.location.pathname.startsWith('/dashboard');

    return !!(isAdminApiUrl || isAdminBrowserPath);
};

/**
 * API Base URL from environment
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
//console.warn('[Axios] Initialized with API_BASE_URL:', API_BASE_URL);

/**
 * Axios instance with default configuration
 */
export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Request interceptor
 * - Adds Authorization header with JWT token
 * - Logs requests in development
 */
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = getStoredToken();
        const adminToken = getStoredAdminToken();

        // Use admin token for dashboard context (checks both API URL and browser location)
        if (isAdminContext(config.url)) {
            if (adminToken && config.headers) {
                config.headers.Authorization = `Bearer ${adminToken}`;
            }
        } else if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        if (import.meta.env.DEV) {
            console.info(`[Axios Request] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`, config.data);
            console.warn(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
        }

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

/**
 * Response interceptor
 * - Handles token refresh on 401 errors
 * - Normalizes error responses
 * - Logs errors in development
 */
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Handle 401 Unauthorized
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // Use isAdminContext to check both API URL and browser location
            const isAdminRequest = isAdminContext(originalRequest.url);

            const refreshToken = isAdminRequest
                ? localStorage.getItem('admin_refresh_token')
                : getStoredRefreshToken();

            if (refreshToken) {
                try {
                    // Attempt to refresh the token
                    const response = await axios.post<{ accessToken: string; refreshToken: string }>(
                        `${API_BASE_URL}/auth/refresh-token`,
                        { refreshToken }
                    );

                    const { accessToken, refreshToken: newRefreshToken } = response.data;

                    // Store new tokens and sync with Redux
                    store.dispatch(setTokens({
                        accessToken,
                        refreshToken: newRefreshToken,
                        isAdmin: !!isAdminRequest
                    }));

                    // Retry the original request
                    if (originalRequest.headers) {
                        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                    }

                    return apiClient(originalRequest);
                } catch (refreshError) {
                    // Refresh failed, logout
                    if (isAdminRequest) {
                        store.dispatch(adminLogout());
                        window.location.href = '/dashboard/admin-login';
                    } else {
                        store.dispatch(logout());
                        window.location.href = '/authentication';
                    }
                    return Promise.reject(refreshError);
                }
            } else {
                // No refresh token, logout
                if (isAdminRequest) {
                    store.dispatch(adminLogout());
                    window.location.href = '/dashboard/admin-login';
                } else {
                    store.dispatch(logout());
                    window.location.href = '/authentication';
                }
            }
        }

        // Log errors in development
        if (import.meta.env.DEV) {
            console.error('[API Error]', {
                status: error.response?.status,
                url: error.config?.url,
                message: error.message,
                data: error.response?.data,
            });
        }

        // Normalize error response
        const errorResponse = {
            status: error.response?.status || 500,
            message: getErrorMessage(error),
            data: error.response?.data,
        };

        return Promise.reject(errorResponse);
    }
);

/**
 * Extract error message from axios error
 */
function getErrorMessage(error: AxiosError): string {
    if (error.response?.data) {
        const data = error.response.data as Record<string, unknown>;

        if (typeof data === 'string') {
            return data;
        }

        if (data.message) {
            if (typeof data.message === 'string') {
                return data.message;
            }
            if (Array.isArray(data.message)) {
                return data.message.join(', ');
            }
        }

        if (data.error && typeof data.error === 'string') {
            return data.error;
        }

        if (data.errors && typeof data.errors === 'object' && data.errors !== null) {
            const errors = Object.values(data.errors).flat();
            return errors.join(', ');
        }
    }

    if (error.message) {
        return error.message;
    }

    return 'An unexpected error occurred';
}

/**
 * Type-safe API response wrapper
 */
export interface ApiResponse<T> {
    data: T;
    status: number;
    message?: string;
}

/**
 * Type-safe API error
 */
export interface ApiError {
    status: number;
    message: string;
    data?: unknown;
}

export default apiClient;
